import React, { useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { FormField, FormService } from '@shared/services/FormServices';
import { RateComponentRef } from './FlatRate';
import { SharedFormArray } from '@shared/components/SharedFormArray';
import { useCurrency } from '@shared/selectors/globalSelectors';

interface TimeOfUseProps {
  data: any;
  hideHeader?: boolean;
  unit?: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const incrementTimeByOneMinute = (timeStr: string) => {
  if (!timeStr) return "";
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  
  let hour = parseInt(parts[0], 10);
  let minute = parseInt(parts[1], 10);
  
  if (isNaN(hour) || isNaN(minute)) return timeStr;
  
  minute += 1;
  if (minute >= 60) {
    minute = 0;
    hour = (hour + 1) % 24;
  }
  
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

const TimeVisualizer = ({ slots }: { slots: any[] }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  return (
    <div className="mb-2 mt-2">
      <div className="flex w-full border border-gray-200 rounded-lg overflow-hidden bg-gray-50 h-12 shadow-sm">
        {hours.map((hour) => {
          let bgColor = 'transparent';
          
          for (const slot of slots) {
            if (!slot.from || !slot.to || !slot.color) continue;
            
            const fromParts = slot.from.split(':');
            const toParts = slot.to.split(':');
            if (fromParts.length < 2 || toParts.length < 2) continue;
            
            const fromHour = parseInt(fromParts[0], 10);
            const fromMinute = parseInt(fromParts[1], 10);
            const toHour = parseInt(toParts[0], 10);
            const toMinute = parseInt(toParts[1], 10);
            
            let isCovered = false;
            
            if (fromHour <= toHour) {
               if (hour > fromHour && hour < toHour) {
                 isCovered = true;
               } else if (hour === fromHour && hour === toHour) {
                 if (toMinute > fromMinute) isCovered = true;
               } else if (hour === fromHour) {
                 isCovered = true;
               } else if (hour === toHour && toMinute > 0) {
                 isCovered = true;
               }
            } else {
               // Cross midnight e.g. 22 to 06
               if (hour >= fromHour || hour < toHour || (hour === toHour && toMinute > 0)) {
                 isCovered = true;
               }
            }
            
            if (isCovered) {
              bgColor = slot.color;
              break;
            }
          }
          
          return (
            <div 
              key={hour} 
              className="flex-1 flex flex-col justify-end border-r border-gray-200 last:border-r-0 relative transition-colors duration-300"
              style={{ backgroundColor: bgColor }}
            >
              <div className="text-[10px] font-medium text-center text-gray-600 bg-white/80 py-1 w-full border-t border-white/50 backdrop-blur-sm">
                {hour.toString().padStart(2, '0')}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const TimeOfUse = forwardRef<RateComponentRef, TimeOfUseProps>(({ data, hideHeader, unit }, ref) => {
  const fields: FormField[] = useMemo(
    () => [
      { 
        name: "color", 
        label: "Color", 
        type: "color", 
        required: true,
        defaultValue: "#3b82f6"
      },
      { 
        name: "slotName", 
        label: "Slot Name", 
        type: "text", 
        required: true,
        placeholder: "e.g. Peak" 
      },
      { 
        name: "from", 
        label: "From", 
        type: "time",
        required: true
      },
      { 
        name: "to", 
        label: "To", 
        type: "time",
        required: true
      },
      { 
        name: "rate", 
        label: "Rate", 
        type: "decimal", 
        required: true,
        allowNegativeValue: false,
        validation: { min: { message: "Negative rates are not allowed" } },
        placeholder: "0.00" 
      },
      { 
        name: "unit", 
        label: "Unit", 
        type: "text", 
        required: true,
        placeholder: "e.g. kWh",
        disabled: true,
        defaultValue: unit,
      }
    ],
    [unit]
  );

  const form = FormService.createForm({
    fields: [
      { name: "slots", type: "form-array", itemFields: fields }
    ],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: (data && Object.keys(data).length > 0) ? data : {
      slots: [{ color: "#3b82f6", slotName: "", from: "", to: "", rate: "", unit: unit || "" }]
    },
  });

  const slots = form.watch("slots") || [];

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      if (!data.slots || data.slots.length === 0) {
        form.reset({
          ...data,
          slots: [{ color: "#3b82f6", slotName: "", from: "", to: "", rate: "", unit: unit || "" }]
        });
      } else {
        form.reset(data);
      }
    }
  }, [data, form]);

  // Update all slots when the unit prop changes
  useEffect(() => {
    if (unit !== undefined) {
      const currentSlots = form.getValues("slots") || [];
      const hasDifferentUnit = currentSlots.some((slot: any) => slot.unit !== unit);
      if (hasDifferentUnit) {
        const updatedSlots = currentSlots.map((slot: any) => ({
          ...slot,
          unit: unit
        }));
        form.reset({
          ...form.getValues(),
          slots: updatedSlots
        });
      }
    }
  }, [unit, form]);

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      // Check if the changed field is a 'to' field inside the slots array
      if (name && name.startsWith("slots.") && name.endsWith(".to")) {
        const parts = name.split(".");
        const index = parseInt(parts[1], 10);
        
        // Push to the end of the event loop to guarantee react-hook-form has committed the state
        setTimeout(() => {
          const livePrevTo = form.getValues(name);
          const slotsArray = form.getValues("slots") || [];
          
          // If there is a next slot, update its 'from' value
          if (!isNaN(index) && slotsArray.length > index + 1) {
            const nextFrom = slotsArray[index + 1]?.from;
            const incrementedFrom = incrementTimeByOneMinute(livePrevTo);
            
            if (incrementedFrom && incrementedFrom !== nextFrom) {
              form.setValue(`slots.${index + 1}.from`, incrementedFrom, { 
                shouldValidate: true,
                shouldDirty: true
              });
            }
          }
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const currency =useCurrency();

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      if (isValid) {
        return form.getValues();
      }
      return null;
    },
    isDirty: () => Object.keys(form.formState.dirtyFields).length > 0
  }));

  const defaultItemFactory = (currentFields: any[]) => {
    const lastSlot = currentFields.length > 0 ? currentFields[currentFields.length - 1] : null;
    const newColor = COLORS[currentFields.length % COLORS.length];
    
    return {
      color: newColor,
      slotName: "",
      from: lastSlot && lastSlot.to ? incrementTimeByOneMinute(lastSlot.to) : "",
      to: "",
      rate: "",
      unit: unit || ""
    };
  };

  return (
    <div> 
      <div className="p-6 bg-white rounded-xl mb-6">
        <div className="flex justify-between items-start mb-4">
          <div className="w-full">
            {!hideHeader && (
              <>
                <h3 className="text-lg font-semibold text-gray-900">Time-of-Use Slots</h3>
                <p className="mb-2 text-sm text-gray-500 mt-1">Define one or more time-of-use slots. Each slot has its own time window across a 24-hour day, name, rate, and color. Slots can overlap or share names — billing distributes consumption across them.</p>
              </>
            )}
            <TimeVisualizer slots={slots} />
          </div>
        </div>
        {slots.length > 0 && (
        <div className="flex flex-wrap items-center gap-6 py-2 px-2">
          {slots.map((slot: any, index: number) => {
            if (!slot.slotName && !slot.rate) return null;
            return (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div 
                  className="w-4 h-4 rounded-sm shadow-sm border border-gray-200" 
                  style={{ backgroundColor: slot.color || 'transparent' }} 
                />
                <span className="font-medium text-gray-700">{slot.slotName || 'Unnamed'}</span>
                {slot.rate && <span className="text-gray-500">{currency}{" "}{slot.rate}</span>}
              </div>
            );
          })}
        </div>
      )}

        <SharedFormArray
          name="slots"
          form={form}
          itemFields={fields}
          variant="table"
          className="p-0 shadow-none mb-0"
          emptyMessage="No time slots configured yet."
          addButtonText="Add Slot"
          minItems={1}
          defaultItemFactory={defaultItemFactory}
          gridTemplateColumns="60px minmax(80px, 1.5fr) minmax(125px, 1.5fr) minmax(125px, 1.5fr) minmax(60px, 1fr) minmax(60px, 1fr) 48px"
        />
      </div>
    </div>
  );
});

TimeOfUse.displayName = "TimeOfUse";
